import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, UserCheck } from "lucide-react";
import { api } from "../../lib/api";
import { nlNum } from "../../lib/isde";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { ContactPreference } from "./ContactPreference";

const PRODUCTS = ["kunststof_kozijnen", "aluminium_kozijnen", "kunststof_schuifpui", "aluminium_schuifpui", "kunststof_deur", "aluminium_deur", "meerdere_kozijnen"];
const EMPTY = { naam: "", email: "", telefoon: "", woonplaats: "", vraag: "", pref: "geen", moment: "geen", privacy: false, website: "" };

const notesFor = (r, vraag) => [
  `Verzoek: laat Maas Kozijn mijn ISDE-situatie beoordelen (uitkomst subsidiecheck ${r.calculation_id}: Persoonlijke controle noodzakelijk).`,
  `Oppervlakten zoals ingevoerd, zonder afronding: ${r.surfaces.categories.map((c) => `${c.name} ${nlNum(c.m2_exact)} m²`).join("; ")}.`,
  vraag.trim() && `Toelichting bezoeker: ${vraag.trim()}`,
].filter(Boolean).join("\n").slice(0, 2000);

const FIELD_ERRORS = { contact_name: "Vul uw naam in.", email: "Vul een geldig e-mailadres in.", phone: "Vul een geldig telefoonnummer in." };
const check = (f) => {
  if (f.naam.trim().length < 2) return FIELD_ERRORS.contact_name;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return FIELD_ERRORS.email;
  const tel = f.telefoon.trim();
  const digits = tel.replace(/\D/g, "");
  if ((tel || f.pref !== "email") && (!/^[+0-9 ()-]{8,20}$/.test(tel) || digits.length < 9 || digits.length > 15)) return FIELD_ERRORS.phone;
  return f.privacy ? "" : "Geef akkoord op de privacyverklaring.";
};
const serverError = (d) => (Array.isArray(d) ? [...new Set(d.map((x) => FIELD_ERRORS[x.loc?.[x.loc.length - 1]] || "Controleer de ingevulde gegevens."))].join(" ") : d);

const Field = ({ id, label, children }) => (
  <div><label htmlFor={id} className="block text-[14px] font-semibold text-ink">{label}</label><div className="mt-1.5">{children}</div></div>
);

export const IsdeReviewRequest = ({ result: r, prefill }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(() => ({ ...EMPTY, woonplaats: prefill?.woonplaats || "" }));
  const [state, setState] = useState({ busy: false, error: "", done: null });
  const meta = useRef({ key: "", at: Date.now() });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const show = () => { if (!meta.current.key) meta.current.key = crypto.randomUUID(); setOpen(true); };
  const submit = async (e) => {
    e.preventDefault();
    if (state.busy) return;
    const invalid = check(f);
    if (invalid) return setState((s) => ({ ...s, error: invalid }));
    setState({ busy: true, error: "", done: null });
    try {
      const { data } = await api.post("/quote-requests", {
        idempotency_key: meta.current.key, lead_type: "contact_request", product_category: PRODUCTS.includes(prefill?.product) ? prefill.product : "anders",
        locality: f.woonplaats.trim(), notes: notesFor(r, f.vraag), contact_name: f.naam.trim(), email: f.email.trim(), phone: f.telefoon.trim(),
        contact_topic: "isde", contact_preference: f.pref, contact_moment: f.pref === "telefoon" ? f.moment : null, isde_review_requested: true,
        consent_privacy: true, source_page: "/isde-subsidie-kozijnen", landing_page: window.location.pathname, referrer: document.referrer.slice(0, 500),
        website: f.website, elapsed_ms: Date.now() - meta.current.at, isde_check_id: r.calculation_id,
      });
      setState({ busy: false, error: "", done: { number: data.lead_number, dup: data.duplicate_submission } });
    } catch (err) {
      setState({ busy: false, error: serverError(err?.response?.data?.detail) || "Versturen is niet gelukt. Probeer het opnieuw of bel ons.", done: null });
    }
  };
  return (
    <>
      <button type="button" onClick={show} className="btn-cta mt-4 w-full sm:w-auto" data-testid="isde-review-request-btn">
        <UserCheck className="h-5 w-5 shrink-0" aria-hidden="true" /> Laat Maas Kozijn mijn situatie beoordelen
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-lg overflow-y-auto rounded-2xl" data-testid="isde-review-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[20px] text-ink">Laat Maas Kozijn uw situatie beoordelen</DialogTitle>
            <DialogDescription className="text-[14px] text-slate-600">Vrijblijvend en zonder verplichte afspraak. Wij bekijken uw oppervlakten en de RVO-regels en nemen contact met u op.</DialogDescription>
          </DialogHeader>
          {state.done ? (
            <div className="rounded-xl bg-emerald-50 p-4 text-[14.5px] text-emerald-900" data-testid="isde-review-request-success">
              <p className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-5 w-5" /> {state.done.dup ? "Uw verzoek voor deze subsidiecheck hadden wij al ontvangen." : "Bedankt, uw verzoek is ontvangen."}</p>
              <p className="mt-1">Wij nemen vrijblijvend contact met u op. Uw referentie: <strong data-testid="isde-review-request-ref">{state.done.number}</strong>.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" noValidate>
              <Field id="isde-rr-naam" label="Naam"><Input id="isde-rr-naam" value={f.naam} onChange={(e) => set("naam", e.target.value)} maxLength={100} autoComplete="name" data-testid="isde-review-request-name" /></Field>
              <Field id="isde-rr-email" label="E-mailadres"><Input id="isde-rr-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} maxLength={120} autoComplete="email" data-testid="isde-review-request-email" /></Field>
              <Field id="isde-rr-tel" label={f.pref === "email" ? "Telefoonnummer (optioneel)" : "Telefoonnummer"}><Input id="isde-rr-tel" type="tel" value={f.telefoon} onChange={(e) => set("telefoon", e.target.value)} maxLength={20} autoComplete="tel" data-testid="isde-review-request-phone" /></Field>
              <ContactPreference pref={f.pref} moment={f.moment} onPref={(v) => set("pref", v)} onMoment={(v) => set("moment", v)} />
              <Field id="isde-rr-plaats" label="Woonplaats (optioneel)"><Input id="isde-rr-plaats" value={f.woonplaats} onChange={(e) => set("woonplaats", e.target.value)} maxLength={80} autoComplete="address-level2" data-testid="isde-review-request-city" /></Field>
              <Field id="isde-rr-vraag" label="Toelichting (optioneel)"><Textarea id="isde-rr-vraag" value={f.vraag} onChange={(e) => set("vraag", e.target.value)} maxLength={800} rows={3} data-testid="isde-review-request-message" /></Field>
              <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />
              <label className="flex items-start gap-2.5 text-[13.5px] text-slate-600">
                <input type="checkbox" checked={f.privacy} onChange={(e) => set("privacy", e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#0E777D]" data-testid="isde-review-request-privacy" />
                <span>Ik ga akkoord met de <Link to="/privacy" target="_blank" className="font-medium text-maas underline">privacyverklaring</Link>. Mijn subsidiecheck ({r.calculation_id}) wordt meegestuurd.</span>
              </label>
              {state.error && <p className="rounded-lg bg-rose-50 px-3 py-2 text-[13.5px] text-rose-800" role="alert" data-testid="isde-review-request-error">{state.error}</p>}
              <button type="submit" disabled={state.busy} className="btn-cta w-full" data-testid="isde-review-request-submit">
                {state.busy && <Loader2 className="h-4 w-4 animate-spin" />} Verstuur vrijblijvend verzoek
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
